import { useReducer, useCallback, useEffect, useRef } from 'react';
import { isCancel } from 'axios';
import { message } from 'antd';
import { instance } from 'store/utils';
import { Errors } from 'utils/errors';

/**
 * @description Handles the **Search on Funding page** logic (query).
 * @returns {{
 *   searchWithFile: (payload: Object) => Promise<void>,
 *   searchWithFilters: (payload: Object) => Promise<void>,
 *   searchWithFiltersAndPagination: (payload: Object) => Promise<void>,
 *   data: Object | null,
 *   dataPaginated: Object | null,
 *   isFetching: boolean,
 *   isEmpty: boolean,
 *   isError: boolean,
 *   error: unknown | null,
 *   searchMode: 'filters' | 'file' | 'pagination',
 * }} Query utilities.
 */
export function useFundingQuery() {
  const abortControllerRef = useRef(null);
  const [state, dispatch] = useReducer(reducer, initialState);

  const searchWithFiltersAndPagination = useCallback(
    async (payload, page = 0, size = 5) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      dispatch({ type: 'QUERY_START', searchMode: 'pagination' });
      try {
        const res = await instance.post(
          `/proposal/funding-programs/search/more?page=${page}&size=${size}`,
          payload,
          { signal: abortControllerRef.current.signal }
        );
        dispatch({ type: 'QUERY_SUCCESS_PAGINATED', payload: res.data });
      } catch (err) {
        if (isCancel(err)) {
          dispatch({ type: 'QUERY_CANCELLED' });
          return;
        }
        const errorMessage = Errors.GenericRetrievalError;
        message.error(errorMessage);
        dispatch({ type: 'QUERY_ERROR', payload: err });
      }
    },
    []
  );

  const searchWithFilters = useCallback(async payload => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    dispatch({ type: 'QUERY_START', searchMode: 'filters' });
    try {
      const res = await instance.post(
        '/proposal/funding-programs/search',
        payload,
        { signal: abortControllerRef.current.signal }
      );
      dispatch({
        type: 'QUERY_SUCCESS',
        payload: { data: res.data, source: 'filters' },
      });
    } catch (err) {
      if (isCancel(err)) {
        dispatch({ type: 'QUERY_CANCELLED' });
        return;
      }
      const errorMessage = Errors.GenericRetrievalError;
      message.error(errorMessage);
      dispatch({ type: 'QUERY_ERROR', payload: err });
    }
  }, []);

  const searchWithFile = useCallback(async id => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    dispatch({ type: 'QUERY_START', searchMode: 'file' });
    try {
      const res = await instance.post(
        `/proposal/funding-programs/search/optimal-proposal?id=${id}`,
        {},
        { signal: abortControllerRef.current.signal }
      );
      dispatch({
        type: 'QUERY_SUCCESS',
        payload: { data: res.data, source: 'file' },
      });
    } catch (err) {
      if (isCancel(err)) {
        dispatch({ type: 'QUERY_CANCELLED' });
        return;
      }
      const errorMessage = Errors.GenericRetrievalError;
      message.error(errorMessage);
      dispatch({ type: 'QUERY_ERROR', payload: err });
    }
  }, []);

  useEffect(() => {
    searchWithFilters({});

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchWithFilters]);

  return {
    searchWithFile,
    searchWithFilters,
    searchWithFiltersAndPagination,
    data: state.data,
    dataPaginated: state.dataPaginated,
    isFetching: state.isFetching,
    isError: state.isError,
    error: state.error,
    isEmpty: state.isEmpty,
    searchMode: state.searchMode,
  };
}

const initialState = {
  data: null,
  dataPaginated: null,
  isFetching: false,
  isError: false,
  error: null,
  isEmpty: false,
  searchMode: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'QUERY_START': {
      return {
        ...state,
        isFetching: true,
        isError: false,
        error: null,
        isEmpty: false,
        searchMode: action.searchMode,
      };
    }
    case 'QUERY_SUCCESS': {
      return {
        ...state,
        data: action.payload.data,
        isFetching: false,
        isError: false,
        error: null,
        isEmpty:
          action.payload.source === 'filters'
            ? Object.values(action.payload.data ?? {}).every(
                item =>
                  item === null ||
                  item === undefined ||
                  (Array.isArray(item) && item.length === 0)
              )
            : (action.payload.data?.fundingPrograms ?? []).length === 0,
      };
    }
    case 'QUERY_SUCCESS_PAGINATED': {
      return {
        ...state,
        dataPaginated: action.payload,
        isFetching: false,
        isError: false,
        error: null,
        isEmpty: action.payload?.totalElements === 0,
      };
    }
    case 'QUERY_ERROR': {
      return {
        ...state,
        data: null,
        dataPaginated: null,
        isFetching: false,
        isError: true,
        error: action.payload,
        isEmpty: false,
      };
    }
    case 'QUERY_CANCELLED': {
      return { ...state, isFetching: false };
    }
    default: {
      return state;
    }
  }
}
